import type { Experiment } from '../types/vuser';
import { formatJobErrorMessage } from './errorUtils';

export type ExperimentStatusKind =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'completed_warnings';

export type ExperimentProgressVariant = 'running' | 'completed' | 'failed' | 'warning';

export interface ExperimentDisplayState {
  statusLabel: string;
  statusKind: ExperimentStatusKind;
  progressVariant: ExperimentProgressVariant;
  showDatasetFailureMessage: boolean;
  datasetFailureMessage: string | null;
  scoreCaption: string | null;
  progressNote: string | null;
}

export function isExperimentDatasetUnavailable(
  experiment: Pick<Experiment, 'dataset_status'>
): boolean {
  const status = experiment.dataset_status;
  return status === 'failed' || status === 'label_failed';
}

export function isExperimentCompletedWithDatasetFailure(
  experiment: Pick<Experiment, 'status' | 'dataset_status'>
): boolean {
  return experiment.status === 'completed' && isExperimentDatasetUnavailable(experiment);
}

export function formatExperimentDatasetFailureMessage(
  errorMessage: string | null | undefined
): string {
  const raw = (errorMessage || '').trim();
  if (!raw) {
    return 'The experiment finished, but the dataset was not created.';
  }

  const formatted = formatJobErrorMessage(raw, 'Dataset was not created.');
  if (/^the experiment finished/i.test(formatted)) return formatted;
  return `The experiment finished, but the dataset was not created. ${formatted}`;
}

export function resolveExperimentDisplayState(experiment: Experiment): ExperimentDisplayState {
  const errorRaw = experiment.error_message?.trim() || '';
  const datasetUnavailable = isExperimentDatasetUnavailable(experiment);
  const completedWithDatasetFailure = isExperimentCompletedWithDatasetFailure(experiment);

  if (completedWithDatasetFailure) {
    return {
      statusLabel: 'Completed with warnings',
      statusKind: 'completed_warnings',
      progressVariant: 'warning',
      showDatasetFailureMessage: true,
      datasetFailureMessage: formatExperimentDatasetFailureMessage(errorRaw),
      scoreCaption: 'Experiment score only — dataset not available for training',
      progressNote: 'Dialogs finished · dataset not created',
    };
  }

  if (experiment.status === 'failed') {
    return {
      statusLabel: 'Failed',
      statusKind: 'failed',
      progressVariant: 'failed',
      showDatasetFailureMessage: Boolean(errorRaw),
      datasetFailureMessage: errorRaw
        ? formatJobErrorMessage(errorRaw, 'Experiment failed')
        : null,
      scoreCaption: null,
      progressNote: null,
    };
  }

  if (experiment.status === 'cancelled') {
    return {
      statusLabel: 'Cancelled',
      statusKind: 'cancelled',
      progressVariant: 'failed',
      showDatasetFailureMessage: Boolean(errorRaw),
      datasetFailureMessage: errorRaw ? formatJobErrorMessage(errorRaw, 'Experiment cancelled') : null,
      scoreCaption: null,
      progressNote: null,
    };
  }

  if (experiment.status === 'completed') {
    return {
      statusLabel: 'Completed',
      statusKind: 'completed',
      progressVariant: 'completed',
      showDatasetFailureMessage: false,
      datasetFailureMessage: null,
      scoreCaption: null,
      progressNote: null,
    };
  }

  if (experiment.status === 'running') {
    return {
      statusLabel: 'Running',
      statusKind: 'running',
      progressVariant: 'running',
      showDatasetFailureMessage: false,
      datasetFailureMessage: null,
      scoreCaption: null,
      progressNote: null,
    };
  }

  if (experiment.status === 'queued') {
    return {
      statusLabel: 'Queued',
      statusKind: 'queued',
      progressVariant: 'running',
      showDatasetFailureMessage: false,
      datasetFailureMessage: null,
      scoreCaption: null,
      progressNote: null,
    };
  }

  const fallbackStatus = String(experiment.status || 'unknown');
  return {
    statusLabel: fallbackStatus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    statusKind: 'queued',
    progressVariant: 'running',
    showDatasetFailureMessage: datasetUnavailable && Boolean(errorRaw),
    datasetFailureMessage:
      datasetUnavailable && errorRaw ? formatExperimentDatasetFailureMessage(errorRaw) : null,
    scoreCaption: null,
    progressNote: null,
  };
}
